import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EyeOutlined from '@ant-design/icons/lib/icons/EyeOutlined';
import StopOutlined from '@ant-design/icons/lib/icons/StopOutlined';

import { opTime } from 'utils/dates';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { OpTable } from 'new-components/DLS/OpTable/OpTable';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpFlex } from 'new-components/DLS/OpFlex/OpFlex';
import { ApiEditButton } from 'new-components/DLS/OpTableCore/components/ApiEditButton';
import { tableStateKeys } from 'new-components/DLS/OpTableCore/constants/tableStateKeys';
import { OpTableRawColumnType } from 'new-components/DLS/OpTableCore/types';
import { useSelectorJs } from 'utils/customHooks';
import { useEndpointPermissions } from 'utils/customHooks/useEndpointPermissions';
import { getOpParams } from 'utils/getOpParams';

import { DATE_TIME_AM_PM_FORMAT } from '../../../../constants/dates';
import { TABLE_HEIGHT } from '../../../../constants/ui';
import {
  fetchRedFlagLogs,
  fetchRedFlags,
  RedFlagState,
} from '../../../../store/slices/redFlagSlice';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import { RedFlag } from '../../../../types/redFlagTypes';
import { formatFullName } from '../../../../utils/utils';

import ExternalRedFlagDetailsModal from './ExternalRedFlagDetailsModal';
import RedFlagDetailsModal from './RedFlagDetailsModal';

interface RedFlagMatchModalProps {
  visible: boolean;
  onClose: () => void;
  externalRedFlags?: ExternalRedFlagRecord[];
  onAddVisitor?: () => void;
}

interface ExternalRedFlagPerson {
  full_name?: string;
  dob?: string;
}

interface ExternalRedFlagRecord {
  id?: string | number;
  person?: ExternalRedFlagPerson;
  category?: string;
}

interface SelectedRedFlaggedVisitor {
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

const RedFlagMatchModal = ({
  visible,
  onClose,
  externalRedFlags,
  onAddVisitor,
}: RedFlagMatchModalProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { hasReadPermission: hasRedFlagRead } =
    useEndpointPermissions('listAllRedFlag');

  const selectedRedFlaggedVisitor = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['redFlags', 'selectedRedFlaggedVisitor']),
  ) as SelectedRedFlaggedVisitor | null;
  const matchingRedFlags = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['redFlags', 'matchingRedFlags']),
  ) as RedFlagState['matchingRedFlags'];
  const fetchRedFlagsLoading = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['redFlags', 'fetchRedFlagsLoading']),
  ) as RedFlagState['fetchRedFlagsLoading'];

  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [selectedRedFlag, setSelectedRedFlag] = useState<RedFlag | null>(null);
  const [selectedExternalRecord, setSelectedExternalRecord] =
    useState<ExternalRedFlagRecord | null>(null);
  const [isExternalDetailsVisible, setIsExternalDetailsVisible] =
    useState(false);

  // Guarded on redFlagReadScopes because the helium GET /redflag route rejects
  // roles without red flag read (e.g. dashboard-only) with a 403.
  useEffect(() => {
    if (orgId && hasRedFlagRead) {
      dispatch(
        fetchRedFlags({
          orgId,
          isMatchingRedFlags: true,
        }),
      );
    }
  }, [dispatch, orgId, hasRedFlagRead]);

  // Filter out expired red flags before filtering by visitor name
  const now = opTime();
  const activeRedFlags = matchingRedFlags.data.filter((item: RedFlag) => {
    if (!item.expireDate) return true; // No expireDate means never expires
    return opTime.utc(item.expireDate).local().isAfter(now);
  });

  // Client-side filtering to match visitor
  const filteredRedFlags = activeRedFlags.filter((redFlag: RedFlag) => {
    const normalizeString = (str?: string | null) =>
      str?.trim().toLowerCase() || '';

    const firstNameMatch =
      normalizeString(redFlag?.firstName) ===
      normalizeString(selectedRedFlaggedVisitor?.firstName);
    const lastNameMatch =
      normalizeString(redFlag?.lastName) ===
      normalizeString(selectedRedFlaggedVisitor?.lastName);

    // Middle name match: if visitor has no middle name, match all red flags
    // Otherwise, red flag middle name must match exactly
    const normalizedRedFlagMiddleName = normalizeString(redFlag?.middleName);
    const normalizedVisitorMiddleName = normalizeString(
      selectedRedFlaggedVisitor?.middleName,
    );

    const middleNameMatch =
      !normalizedVisitorMiddleName ||
      normalizedRedFlagMiddleName === normalizedVisitorMiddleName;

    return firstNameMatch && lastNameMatch && middleNameMatch;
  });

  const transformedData = filteredRedFlags.map((item: RedFlag) => ({
    ...item,
    effectiveDate: item.effectiveDate
      ? opTime(item.effectiveDate)
          .utc(true)
          .local()
          .format(DATE_TIME_AM_PM_FORMAT)
      : '--',
    fullName: formatFullName(item.firstName, item.middleName, item.lastName),
  }));

  const formatDate = (dateStr: string) => {
    if (!dateStr) return null;
    try {
      return dateStr.length === 8
        ? dateStr.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3') // e.g. 19960600 will be converted to 1996-06-00
        : dateStr;
    } catch {
      return null;
    }
  };

  const columns: OpTableRawColumnType<RedFlag>[] = [
    {
      dataIndex: 'levelName',
      label: t('visitors.redFlag.matchModal.columns.levelName'),
      render: (text: string) => {
        if (text === 'Blocklist') {
          return (
            <span>
              <StopOutlined style={{ color: 'rgba(242,103,87,1.0)' }} />{' '}
              {t('visitors.redFlag.matchModal.blocklist')}
            </span>
          );
        }
        if (text === 'Watchlist') {
          return (
            <span>
              <EyeOutlined style={{ color: '#FFD54F' }} />{' '}
              {t('visitors.redFlag.matchModal.watchlist')}
            </span>
          );
        }
        return text;
      },
      // filter: {
      //     type: 'multiSelect',
      //     options: [
      //         { label: 'Blocklist', value: 'Blocklist' },
      //         { label: 'Watchlist', value: 'Watchlist' }
      //     ],
      // },
      sorter: (a, b) => (a.levelName || '').localeCompare(b.levelName || ''), // Alphabetical sorting with null check
    },
    {
      dataIndex: 'fullName',
      label: t('visitors.redFlag.matchModal.columns.fullName'),
      // filter: {
      //     type: 'input',
      // },
      sorter: (a, b) => (a.fullName || '').localeCompare(b.fullName || ''), // Alphabetical sorting with null check
    },
    {
      dataIndex: 'effectiveDate',
      label: t('visitors.redFlag.matchModal.columns.effectiveDate'),
      // filter: {
      //     type: 'input',
      // },
      sorter: (a, b) =>
        (a.effectiveDate || '').localeCompare(b.effectiveDate || ''), // Alphabetical sorting with null check
    },
  ];

  const handleOpenInternalDetails = (record: RedFlag) => {
    setSelectedRedFlag(record);
    dispatch(fetchRedFlagLogs({ orgId, redFlagId: record.id }));
    setIsDetailsModalVisible(true);
  };

  const externalColumns: OpTableRawColumnType<ExternalRedFlagRecord>[] = [
    {
      label: t('visitors.redFlag.matchModal.columns.fullName'),
      dataIndex: ['person', 'full_name'],
      key: 'full_name',
      sorter: (a, b) =>
        (a.person?.full_name || '').localeCompare(b.person?.full_name || ''),
    },
    {
      label: t('visitors.redFlag.matchModal.columns.dob'),
      dataIndex: ['person', 'dob'],
      key: 'dob',
      render: (dob: string) => (dob ? formatDate(dob) : '--'),
      sorter: (a, b) =>
        (a.person?.dob || '').localeCompare(b.person?.dob || ''),
    },
    {
      label: t('visitors.redFlag.matchModal.columns.category'),
      dataIndex: 'category',
      key: 'category',
      sorter: (a, b) => (a.category || '').localeCompare(b.category || ''),
    },
  ];

  const handleOpenExternalDetails = (record: ExternalRedFlagRecord) => {
    setSelectedExternalRecord(record);
    setIsExternalDetailsVisible(true);
  };

  const hasMatchingRedFlags = filteredRedFlags.length > 0;
  const hasExternalRedFlags = externalRedFlags && externalRedFlags?.length > 0;

  const modalTitle =
    hasMatchingRedFlags && hasExternalRedFlags
      ? t('visitors.redFlag.matchModal.title')
      : hasMatchingRedFlags
        ? t('visitors.redFlag.matchModal.title')
        : t('visitors.redFlag.matchModal.externalTitle');

  return (
    <>
      <OpModal
        title={modalTitle}
        open={visible}
        onCancel={onClose}
        width={720}
        footer={
          onAddVisitor ? (
            <OpFlex gap={8} justify="flex-end">
              <OpButton onClick={onClose}>
                {t('visitors.redFlag.matchModal.cancel')}
              </OpButton>
              <OpButton type="primary" onClick={onAddVisitor}>
                {t('visitors.redFlag.matchModal.addVisitor')}
              </OpButton>
            </OpFlex>
          ) : (
            <OpFlex gap={8} justify="flex-end">
              <OpButton type="primary" onClick={onClose}>
                {t('visitors.redFlag.matchModal.close')}
              </OpButton>
            </OpFlex>
          )
        }
      >
        <OpSpace
          orientation="vertical"
          style={{ overflowY: 'auto', maxHeight: '70vh', width: '100%' }}
        >
          {hasMatchingRedFlags && (
            <OpTable
              uiStateKey={tableStateKeys.VisitorRedFlagMatchInternalTable}
              dataSource={transformedData}
              columns={columns}
              onRowClick={handleOpenInternalDetails}
              rowActions={(record: RedFlag) => [
                <ApiEditButton
                  key="view"
                  hasWritePermission={false}
                  onClick={() => {
                    handleOpenInternalDetails(record);
                  }}
                />,
              ]}
              height={TABLE_HEIGHT}
              allowGlobalSearch={false}
              loading={fetchRedFlagsLoading}
              allowExport={false}
              allowShowHideColumns={false}
            />
          )}

          {hasMatchingRedFlags && hasExternalRedFlags && (
            <div style={{ marginTop: '16px' }} />
          )}

          {hasMatchingRedFlags && hasExternalRedFlags && (
            <h4>
              <b>{t('visitors.redFlag.matchModal.externalSectionTitle')}</b>
            </h4>
          )}
          {hasExternalRedFlags && (
            <OpTable
              uiStateKey={tableStateKeys.VisitorRedFlagMatchExternalTable}
              dataSource={externalRedFlags || []}
              columns={externalColumns}
              onRowClick={handleOpenExternalDetails}
              rowActions={(record: ExternalRedFlagRecord) => [
                <ApiEditButton
                  key="view"
                  hasWritePermission={false}
                  onClick={() => {
                    handleOpenExternalDetails(record);
                  }}
                />,
              ]}
              height={TABLE_HEIGHT}
              allowGlobalSearch={false}
              allowExport={false}
              allowShowHideColumns={false}
            />
          )}
        </OpSpace>
      </OpModal>

      {isDetailsModalVisible && (
        <RedFlagDetailsModal
          open={isDetailsModalVisible}
          onClose={() => setIsDetailsModalVisible(false)}
          redFlagData={selectedRedFlag}
        />
      )}

      {isExternalDetailsVisible && (
        <ExternalRedFlagDetailsModal
          open={isExternalDetailsVisible}
          onClose={() => setIsExternalDetailsVisible(false)}
          record={selectedExternalRecord}
        />
      )}
    </>
  );
};

export default RedFlagMatchModal;
